import { useGetList } from "ra-core";
import type { Deal, Task } from "../types";
import { isClosedStage } from "../deals/funnels";

type MeetingSummary = { id: string; starts_at: string };
type EmailSummary = { id: string; status: string };

const formatPipeline = (deals: Deal[]) => {
  const totals = deals.reduce<Record<string, number>>((result, deal) => {
    const currency = deal.currency ?? "USD";
    result[currency] = (result[currency] ?? 0) + Number(deal.amount ?? 0);
    return result;
  }, {});

  const values = Object.entries(totals).map(([currency, amount]) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount),
  );
  return values.length ? values.join(" · ") : "$0";
};

export const DashboardMetrics = () => {
  const now = new Date();
  const endOfWeek = new Date(now);
  endOfWeek.setDate(now.getDate() + 7);

  const companies = useGetList("companies", {
    pagination: { page: 1, perPage: 1 },
    filter: { "archived_at@is": null },
  });
  const contacts = useGetList("contacts", {
    pagination: { page: 1, perPage: 1 },
    filter: { "archived_at@is": null },
  });
  const deals = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
    filter: { "archived_at@is": null },
  });
  const meetings = useGetList<MeetingSummary>("meetings", {
    pagination: { page: 1, perPage: 1000 },
    filter: { "starts_at@gte": now.toISOString() },
  });
  const tasks = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 1000 },
    filter: {},
  });
  const emails = useGetList<EmailSummary>("email_messages", {
    pagination: { page: 1, perPage: 1 },
    filter: { status: "review" },
  });

  const openDeals = (deals.data ?? []).filter(
    (deal) => !isClosedStage(deal.stage),
  );
  const overdueTasks = (tasks.data ?? []).filter(
    (task) =>
      task.status !== "completed" &&
      task.status !== "cancelled" &&
      task.due_date &&
      new Date(task.due_date) < now,
  );
  const weekTasks = (tasks.data ?? []).filter(
    (task) =>
      task.status !== "completed" &&
      task.status !== "cancelled" &&
      task.due_date &&
      new Date(task.due_date) >= now &&
      new Date(task.due_date) <= endOfWeek,
  );

  const metrics = [
    ["Empresas activas", companies.total ?? 0],
    ["Contactos", contacts.total ?? 0],
    ["Oportunidades abiertas", openDeals.length],
    ["Valor del pipeline", formatPipeline(openDeals)],
    ["Reuniones próximas", meetings.total ?? 0],
    ["Tareas vencidas", overdueTasks.length],
    ["Tareas esta semana", weekTasks.length],
    ["Correos por revisar", emails.total ?? 0],
  ];

  return (
    <section
      aria-label="Resumen comercial"
      className="grid grid-cols-2 gap-px overflow-hidden border bg-border md:grid-cols-4"
    >
      {metrics.map(([label, value]) => (
        <div key={label} className="min-h-20 bg-background p-3">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="mt-2 break-words text-lg font-semibold">{value}</p>
        </div>
      ))}
    </section>
  );
};
